import { FormControl, FormGroup, Validators } from '@angular/forms';
import { InternalFormValidators } from '@internal-libs/form-validators';
import { IEmployee } from '@internal-models/employee.model';
import {
	CommunicationOnEventTrigger,
	CustomIntervalType,
	EventAttendanceType,
	EventInterval,
	EventLocationType,
	IEvent,
} from '@internal-models/event.model';
import { SharedFormValidators } from '@shared-libs/form-validators';
import { IFormGroup } from '@shared-libs/interfaces';
import { format, set } from 'date-fns';

export type IEventForm = IEvent & { startTime: string; endTime: string };

export class EventFormFactory {
	public static create(
		event?: Partial<IEvent>,
		date?: Date
	): IFormGroup<IEvent & { startTime: string; endTime: string }> {
		const formGroup = new FormGroup(
			{
				id: new FormControl(event?.id),
				name: new FormControl(event?.name, [SharedFormValidators.required, Validators.maxLength(255)]),

				startsAt: new FormControl(
					event?.startsAt || date ? new Date(event?.CalendarEvent.startsAt || date) : null
				),
				startTime: new FormControl(
					event?.startsAt || date ? format(new Date(event?.CalendarEvent.startsAt || date), 'HH:mm') : null
				),
				endTime: new FormControl(event?.endsAt ? format(new Date(event.CalendarEvent.endsAt), 'HH:mm') : null),

				intervalEndsAt: new FormControl(event?.intervalEndsAt ? new Date(event.intervalEndsAt) : null, [
					SharedFormValidators.date,
					SharedFormValidators.isBefore(new Date('2027-01-01T00:00:00')),
				]),
				reservationInterval: new FormControl(event?.reservationInterval ?? 30),
				maxRegistrations: new FormControl(event?.maxRegistrations ?? 1),
				description: new FormControl(event?.description, [Validators.maxLength(2048)]),
				color: new FormControl(event?.color, [SharedFormValidators.required, InternalFormValidators.colorHex]),
				interval: new FormControl(event?.interval ?? EventInterval.OneTime, [SharedFormValidators.required]),
				customIntervalType: new FormControl(event?.customIntervalType ?? CustomIntervalType.Weeks),
				customIntervalNumber: new FormControl(event?.customIntervalNumber ?? 2),
				attendanceType: new FormControl(event?.attendanceType, [SharedFormValidators.required]),
				isLoanLocation: new FormControl(event?.isLoanLocation ?? false),
				digibankObjective: new FormControl(event?.digibankObjective, [SharedFormValidators.required]),
				EventTypeId: new FormControl(event?.EventTypeId, [SharedFormValidators.required]),
				Employees: new FormControl((event?.Employees ?? []).map((employee: IEmployee) => employee.id)),
				locationType: new FormControl(event?.locationType, [SharedFormValidators.required]),
				url: new FormControl(event?.url, [SharedFormValidators.url]),
				LocationId: new FormControl(event?.LocationId),
				DigibankAgencyId: new FormControl(event?.DigibankAgencyId, [SharedFormValidators.required]),
				Lessons: new FormControl(event?.Lessons || []),
				replyTo: new FormControl(event?.replyTo, [SharedFormValidators.email]),
				telephone: new FormControl(event?.telephone, [SharedFormValidators.phone]),
				isPublicEvent: new FormControl(event?.isPublicEvent ?? false),
				publicInfo: new FormControl(event?.publicInfo),
				hasPublicReservation: new FormControl(event?.hasPublicReservation ?? false),
				uitDatabaseLabels: new FormControl(event?.uitDatabaseLabels),
				CommunicationTemplateForCreateReservationIds: new FormControl(
					event?.CToE?.filter(
						(communicationTemplateOnEvent) =>
							communicationTemplateOnEvent.trigger === CommunicationOnEventTrigger.Create
					)?.map((communicationTemplateOnEvent) => communicationTemplateOnEvent.CommunicationTemplate.id)
				),
				CommunicationTemplateForUpdateReservationIds: new FormControl(
					event?.CToE?.filter(
						(communicationTemplateOnEvent) =>
							communicationTemplateOnEvent.trigger === CommunicationOnEventTrigger.Update
					)?.map((communicationTemplateOnEvent) => communicationTemplateOnEvent.CommunicationTemplate.id)
				),
				CommunicationTemplateForCancelReservationIds: new FormControl(
					event?.CToE?.filter(
						(communicationTemplateOnEvent) =>
							communicationTemplateOnEvent.trigger === CommunicationOnEventTrigger.Cancel
					)?.map((communicationTemplateOnEvent) => communicationTemplateOnEvent.CommunicationTemplate.id)
				),
				CommunicationTemplateForRemindReservationIds: new FormControl(
					event?.CToE?.filter(
						(communicationTemplateOnEvent) =>
							communicationTemplateOnEvent.trigger === CommunicationOnEventTrigger.Reminder
					)?.map((communicationTemplateOnEvent) => communicationTemplateOnEvent.CommunicationTemplate.id)
				),
			},
			{
				validators: InternalFormValidators.dateBeforeDate(
					'startsAt',
					'intervalEndsAt',
					'INTERVAL_ENDS_BEFORE_EVENT_STARTS'
				),
			}
		) as IFormGroup;

		const reservationIntervalValidators = [
			SharedFormValidators.required,
			SharedFormValidators.number(10, 60),
			InternalFormValidators.reservationInterval,
		];

		formGroup.get('attendanceType').valueChanges.subscribe(() => {
			if (formGroup.get('attendanceType').value === EventAttendanceType.Reservation) {
				formGroup.get('reservationInterval').setValidators(reservationIntervalValidators);
				formGroup.get('reservationInterval').updateValueAndValidity();
			} else {
				formGroup.get('reservationInterval').setValidators([]);
				formGroup.get('reservationInterval').updateValueAndValidity();
			}

			if (formGroup.get('attendanceType').value === EventAttendanceType.Lessons) {
				formGroup.get('Lessons').setValidators([SharedFormValidators.required]);
				formGroup.get('Lessons').updateValueAndValidity();

				formGroup.get('startsAt').setValidators([]);
				formGroup.get('startsAt').updateValueAndValidity();

				formGroup.get('startTime').setValidators([]);
				formGroup.get('startTime').updateValueAndValidity();

				formGroup.get('endTime').setValidators([]);
				formGroup.get('endTime').updateValueAndValidity();
			} else {
				formGroup.get('Lessons').setValidators([]);
				formGroup.get('Lessons').updateValueAndValidity();

				formGroup.get('startsAt').setValidators([SharedFormValidators.required, SharedFormValidators.date]);
				formGroup.get('startsAt').updateValueAndValidity();

				formGroup.get('startTime').setValidators([SharedFormValidators.required]);
				formGroup.get('startTime').updateValueAndValidity();

				formGroup.get('endTime').setValidators([SharedFormValidators.required]);
				formGroup.get('endTime').updateValueAndValidity();
			}
		});

		if (formGroup.get('attendanceType').value === EventAttendanceType.Reservation) {
			formGroup.get('reservationInterval').setValidators(reservationIntervalValidators);
			formGroup.get('reservationInterval').updateValueAndValidity();
		}
		if (formGroup.get('attendanceType').value === EventAttendanceType.Lessons) {
			formGroup.get('startsAt').setValidators([]);
			formGroup.get('startsAt').updateValueAndValidity();

			formGroup.get('startTime').setValidators([]);
			formGroup.get('startTime').updateValueAndValidity();

			formGroup.get('endTime').setValidators([]);
			formGroup.get('endTime').updateValueAndValidity();
		} else {
			formGroup.get('startsAt').setValidators([SharedFormValidators.required, SharedFormValidators.date]);
			formGroup.get('startsAt').updateValueAndValidity();

			formGroup.get('startTime').setValidators([SharedFormValidators.required]);
			formGroup.get('startTime').updateValueAndValidity();

			formGroup.get('endTime').setValidators([SharedFormValidators.required]);
			formGroup.get('endTime').updateValueAndValidity();
		}

		const setCustomIntervalValidators = (enable: boolean) => {
			formGroup.get('customIntervalType').setValidators(enable ? [SharedFormValidators.required] : []);
			formGroup
				.get('customIntervalNumber')
				.setValidators(enable ? [SharedFormValidators.required, SharedFormValidators.number(1)] : []);
			formGroup.get('customIntervalType').updateValueAndValidity();
			formGroup.get('customIntervalNumber').updateValueAndValidity();
		};

		const addIntervalEndsAtRequiredValidator = (enable: boolean) => {
			if (enable) {
				formGroup.get('intervalEndsAt').addValidators(SharedFormValidators.required);
			} else {
				formGroup.get('intervalEndsAt').removeValidators(SharedFormValidators.required);
			}
		};

		formGroup.get('interval').valueChanges.subscribe(() => {
			if (formGroup.get('interval').value === EventInterval.Custom) {
				setCustomIntervalValidators(true);
				addIntervalEndsAtRequiredValidator(true);
			} else if (formGroup.get('interval').value !== EventInterval.OneTime) {
				addIntervalEndsAtRequiredValidator(true);
			} else {
				setCustomIntervalValidators(false);
				addIntervalEndsAtRequiredValidator(false);
				formGroup.get('intervalEndsAt').reset();
			}
		});
		if (formGroup.get('interval').value === EventInterval.Custom) {
			setCustomIntervalValidators(true);
			addIntervalEndsAtRequiredValidator(true);
		} else if (formGroup.get('interval').value !== EventInterval.OneTime) {
			addIntervalEndsAtRequiredValidator(true);
		}

		const setLocationOrUrlValidators = () => {
			if (formGroup.get('locationType').value === EventLocationType.OnLocation) {
				formGroup.get('LocationId').addValidators(SharedFormValidators.required);
				formGroup.get('url').removeValidators(SharedFormValidators.required);
				formGroup.get('url').patchValue(null);
			} else if (formGroup.get('locationType').value === EventLocationType.Online) {
				formGroup.get('LocationId').removeValidators(SharedFormValidators.required);
				formGroup.get('LocationId').patchValue(null);
				formGroup.get('url').addValidators(SharedFormValidators.required);
			}
			formGroup.get('LocationId').updateValueAndValidity();
			formGroup.get('url').updateValueAndValidity();
		};

		formGroup.get('locationType').valueChanges.subscribe(() => {
			setLocationOrUrlValidators();
		});
		setLocationOrUrlValidators();

		return formGroup;
	}

	public static getValue(form: IFormGroup<IEventForm>): Partial<IEvent> {
		if (form.get('attendanceType').value !== EventAttendanceType.Lessons) {
			const [startHours, startMinutes] = form.value.startTime.split(':');
			const [endHours, endMinutes] = form.value.endTime.split(':');
			return {
				...form.value,
				startsAt: set(form.value.startsAt, {
					hours: parseInt(startHours),
					minutes: parseInt(startMinutes),
					seconds: 0,
				}).toISOString(),
				endsAt: set(form.value.startsAt, {
					hours: parseInt(endHours),
					minutes: parseInt(endMinutes),
					seconds: 0,
				}).toISOString(),
			} as unknown as Partial<IEvent>;
		} else {
			return {
				...form.value,
				startsAt: null,
				endsAt: null,
				startTime: null,
				endTime: null,
			} as Partial<IEvent>;
		}
	}
}
